import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { lobbyService } from '../services/lobbyService';
import { wordService } from '../services/wordService';
import { teamService } from '../services/teamService';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import './MultiplayerLobby.css';

const ROLES = [
  { value: 'giver1', label: 'Speaker 1', icon: '🗣️', desc: 'Dai gli indizi per primo' },
  { value: 'giver2', label: 'Speaker 2', icon: '🎙️', desc: 'Dai gli indizi per secondo' },
  { value: 'guesser', label: 'Guesser', icon: '🎯', desc: 'Ascolta e indovina la parola' },
];

const getRoleLabel = (r) => ROLES.find((x) => x.value === r)?.label ?? '—';

const MultiplayerLobby = () => {
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const { currentUser } = useAuth();
  const [view, setView] = useState('menu'); // menu | create | join | lobby | team
  const [playerName, setName] = useState('');
  const [inputCode, setInputCode] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [gameData, setGameData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const unsubRef = useRef(null);
  const isNavigatingToGame = useRef(false);
  const hasLeftTeamSession = useRef(false);
  const roomCodeRef = useRef('');
  // Always use ref for myId to avoid stale closure issues
  const myIdRef = useRef('');

  // Keep refs in sync
  useEffect(() => {
    roomCodeRef.current = roomCode;
  }, [roomCode]);

  useEffect(() => {
    if (currentUser?.uid) {
      myIdRef.current = currentUser.uid;
    }
  }, [currentUser]);

  const subscribe = useCallback(
    (code, uid) => {
      if (unsubRef.current) unsubRef.current();
      unsubRef.current = lobbyService.subscribeToGame(code, (data) => {
        if (data.status === 'playing' || data.status === 'countdown') {
          if (unsubRef.current) {
            unsubRef.current();
            unsubRef.current = null;
          }
          isNavigatingToGame.current = true;
          navigate('/multiplayer/game', {
            state: {
              code,
              myId: uid,
              myRole: data.players.find((p) => p.id === uid)?.role,
            },
          });
          return;
        }
        setGameData(data);
      });
    },
    [navigate],
  );

  // ── One-time setup effect: runs once per user/navState ─────────────────
  useEffect(() => {
    if (!currentUser) return;

    const uid = currentUser.uid;
    myIdRef.current = uid;
    setName(currentUser.displayName || '');

    const initTeamSession = async () => {
      setLoading(true);
      try {
        const teamId = navState.teamId;
        const teamSnap = await getDoc(doc(db, 'teams', teamId));
        if (!teamSnap.exists()) throw new Error('Squadra non trovata');
        const team = { id: teamSnap.id, ...teamSnap.data() };

        const myTeamRole = teamService.getUserRole(team, uid);
        let lobbyRole = null;
        if (myTeamRole === 'speaker1') lobbyRole = 'giver1';
        if (myTeamRole === 'speaker2') lobbyRole = 'giver2';
        if (myTeamRole === 'guesser') lobbyRole = 'guesser';

        const targetCode = await teamService.claimMultiplayerSession(
          teamId,
          uid,
          lobbyService.createRoom.bind(lobbyService),
          lobbyService.roomExists.bind(lobbyService),
          currentUser.displayName || uid,
        );

        // Join the authoritative lobby (idempotent: safe if already a member)
        await lobbyService.joinRoom(targetCode, uid, currentUser.displayName || '');

        if (lobbyRole) {
          await lobbyService.assignRole(targetCode, uid, lobbyRole);
        }

        roomCodeRef.current = targetCode;
        setRoomCode(targetCode);
        subscribe(targetCode, uid);
        setView('lobby');
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    };

    if (navState?.rejoinCode) {
      const rc = navState.rejoinCode;
      roomCodeRef.current = rc;
      setRoomCode(rc);
      subscribe(rc, uid);
      setView('lobby');
    } else if (navState?.teamId && !hasLeftTeamSession.current) {
      initTeamSession();
    }

    const handleBeforeUnload = () => {
      if (roomCodeRef.current && !isNavigatingToGame.current) {
        lobbyService.leaveRoom(roomCodeRef.current, myIdRef.current);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
      if (roomCodeRef.current && !isNavigatingToGame.current) {
        lobbyService.leaveRoom(roomCodeRef.current, myIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navState]);
  // NOTE: intentionally omitting `subscribe` from deps — it's stable (useCallback with [navigate])
  // and adding roomCode/view would re-run the effect (and re-call leaveRoom) on every state change.

  const handleCreate = async () => {
    if (!playerName.trim()) return setError("Devi effettuare l'accesso per giocare in multiplayer");
    const uid = currentUser?.uid;
    if (!uid) return setError("Devi effettuare l'accesso per giocare in multiplayer");
    setLoading(true);
    setError('');
    try {
      const code = await lobbyService.createRoom(uid, playerName.trim());
      roomCodeRef.current = code;
      setRoomCode(code);
      subscribe(code, uid);
      setView('lobby');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleJoin = async () => {
    if (!playerName.trim()) return setError("Devi effettuare l'accesso per giocare in multiplayer");
    if (!inputCode.trim()) return setError('Inserisci il codice stanza');
    const uid = currentUser?.uid;
    if (!uid) return setError("Devi effettuare l'accesso per giocare in multiplayer");
    setLoading(true);
    setError('');
    try {
      const normalizedCode = inputCode.trim().toUpperCase();
      await lobbyService.joinRoom(normalizedCode, uid, playerName.trim());
      roomCodeRef.current = normalizedCode;
      setRoomCode(normalizedCode);
      subscribe(normalizedCode, uid);
      setView('lobby');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  // Always derive effectiveId from currentUser to avoid stale state
  const effectiveId = currentUser?.uid ?? '';

  const handleRoleSelect = (role) => {
    if (!effectiveId) return;
    const taken = gameData?.players?.find((p) => p.id !== effectiveId && p.role === role);
    if (taken) return setError(`Ruolo già occupato da ${taken.name}`);
    setError('');

    const myCurrentRole = gameData?.players?.find((p) => p.id === effectiveId)?.role;
    const newRole = myCurrentRole === role ? null : role;
    lobbyService.assignRole(roomCode, effectiveId, newRole);
  };

  const handleShuffle = async () => {
    if (gameData?.players?.length !== 3) return;
    setLoading(true);
    try {
      await lobbyService.shuffleRoles(roomCode);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleStartGame = async () => {
    setLoading(true);
    try {
      const [words, phraseWords] = await Promise.all([
        wordService.getWords(80),
        wordService.getPhraseWords(20),
      ]);
      if (!roomCode) throw new Error('Codice stanza mancante');
      await lobbyService.startCountdown(roomCode, words, phraseWords);
    } catch (e) {
      console.error('Error starting game:', e);
      setError(e.message);
    }
    setLoading(false);
  };

  const myPlayer = gameData?.players?.find((p) => p.id === effectiveId);
  const isHost = gameData?.hostId === effectiveId;
  const allHaveRoles =
    gameData?.players?.length === 3 &&
    gameData.players.every((p) => p.role) &&
    new Set(gameData.players.map((p) => p.role)).size === 3;

  // ── Menu ──────────────────────────────────────────
  if (view === 'menu') {
    return (
      <div className="lobby-page">
        <button className="lobby-back" onClick={() => navigate('/')}>
          ← Home
        </button>
        <div className="lobby-card u-scale-in">
          <h1 className="lobby-card__title">Multiplayer</h1>

          {error ? <p className="lobby-error">{error}</p> : null}

          <div className="lobby-menu-btns">
            <button
              className="lobby-btn lobby-btn--primary"
              onClick={handleCreate}
              disabled={loading}
              id="btn-create"
            >
              {loading ? '...' : '🏠 Crea Stanza'}
            </button>
            <button
              className="lobby-btn lobby-btn--secondary"
              onClick={() => setView('join')}
              id="btn-go-join"
            >
              🚪 Entra in una Stanza
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Join ─────────────────────────────────────────
  if (view === 'join') {
    return (
      <div className="lobby-page">
        <button
          className="lobby-back"
          onClick={() => {
            setView('menu');
            setError('');
          }}
        >
          ← Indietro
        </button>
        <div className="lobby-card u-scale-in">
          <h1 className="lobby-card__title">Entra nella Stanza</h1>

          <div className="lobby-name-row">
            <label className="lobby-label">Codice Stanza</label>
            <input
              className="lobby-input lobby-input--code"
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
              placeholder="ABCD"
              maxLength={4}
              id="input-code"
            />
          </div>

          {error ? <p className="lobby-error">{error}</p> : null}

          <button
            className="lobby-btn lobby-btn--primary"
            onClick={handleJoin}
            disabled={loading}
            id="btn-join"
          >
            {loading ? '...' : 'ENTRA'}
          </button>
        </div>
      </div>
    );
  }

  // ── Lobby ─────────────────────────────────────────
  if (view === 'lobby') {
    // Show loading while waiting for Firestore snapshot
    if (!gameData) {
      return (
        <div className="lobby-page">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--color-text-muted)',
            }}
          >
            <p>Connessione alla stanza…</p>
          </div>
        </div>
      );
    }
    return (
      <div className="lobby-page lobby-page--room">
        <button
          className="lobby-back"
          onClick={async () => {
            isNavigatingToGame.current = false;
            const wasDeleted = await lobbyService.leaveRoom(roomCode, effectiveId);
            if (unsubRef.current) unsubRef.current();

            if (navState?.teamId) {
              if (wasDeleted) {
                await teamService.clearActiveSession(navState.teamId);
              }
            }

            // Clear refs to prevent the cleanup effect from running again
            roomCodeRef.current = '';
            hasLeftTeamSession.current = true;
            setRoomCode('');
            setGameData(null);

            setView('menu');
            if (navState?.teamId) navigate(`/teams/${navState.teamId}`);
          }}
        >
          ← Indietro
        </button>

        {isHost ? (
          <div className="lobby-header-actions">
            <button
              className="start-game-btn-top"
              onClick={handleStartGame}
              disabled={loading || !allHaveRoles}
              id="btn-start-multi"
            >
              {loading ? 'Avvio...' : '▶ AVVIA'}
            </button>
          </div>
        ) : null}

        <header className="lobby-room-header-top">
          <div className="lobby-room-code-card">
            <p className="lobby-room-label">CODICE STANZA</p>
            <h1 className="lobby-room-code">{roomCode}</h1>
          </div>
        </header>

        <div className="lobby-layout-split">
          {/* Left Column: Players */}
          <section className="lobby-panel-side lobby-panel-side--left">
            <div className="section-v">
              <h2 className="panel-title">Giocatori</h2>
              <div
                className={`lobby-players-vertical ${isHost ? 'lobby-players-vertical--host' : ''}`}
              >
                {[0, 1, 2].map((i) => {
                  const p = gameData?.players?.[i];
                  return (
                    <div key={i} className={`player-slot-v ${p ? 'player-slot-v--filled' : ''}`}>
                      <div className="player-slot-v__avatar">
                        {p ? p.name.charAt(0).toUpperCase() : '?'}
                      </div>
                      <div className="player-slot-v__info">
                        <span className="player-slot-v__name">{p ? p.name : 'Libero'}</span>
                        {p?.role ? (
                          <span className="player-slot-v__role">{getRoleLabel(p.role)}</span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Right Column: Roles & Start */}
          <section className="lobby-panel-side lobby-panel-side--right">
            {myPlayer ? (
              <div className="section-v">
                <div className="section-header-row">
                  <h2 className="panel-title">Scegli Ruolo</h2>
                  {isHost ? (
                    <button
                      className="shuffle-btn"
                      onClick={handleShuffle}
                      disabled={loading || gameData?.players?.length !== 3}
                      title="Assegna ruoli a caso"
                    >
                      🎲 Shuffle
                    </button>
                  ) : null}
                </div>
                <div className="role-grid-v">
                  {ROLES.map((r) => {
                    const taken = gameData?.players?.find(
                      (p) => p.id !== effectiveId && p.role === r.value,
                    );
                    const selected = myPlayer.role === r.value;
                    return (
                      <button
                        key={r.value}
                        className={`role-card-v ${selected ? 'role-card-v--selected' : ''} ${taken ? 'role-card-v--taken' : ''}`}
                        onClick={() => handleRoleSelect(r.value)}
                        disabled={!!taken}
                        id={`role-${r.value}`}
                      >
                        <span className="role-card-v__icon">{r.icon}</span>
                        <div className="role-card-v__text">
                          <span className="role-card-v__label">{r.label}</span>
                          <span className="role-card-v__desc">
                            {taken ? `(${taken.name})` : r.desc}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    );
  }

  return null;
};

export default MultiplayerLobby;
